import UserCompanies from "@/components/Register/userCompanies.component";
import UserConsent from "@/components/Register/userConsent.component";
import UserDetails from "@/components/Register/userDetails.component";
import {
  COUNTRIES,
  EMPTYADDRESS,
  INDUSTRY_TYPES,
  LANGUAGES,
} from "@/constants/util.constants";
import { sendRegisterUser } from "@/models/socket/register.model";
import { Alert, AlertDescription } from "@/shadcn/components/ui/alert";
import { Button } from "@/shadcn/components/ui/button";
import { Card, CardContent } from "@/shadcn/components/ui/card";
import { SendRegisterUser, SendRegisterUserCompany } from "@/types";
import { validatePassword } from "@/utils/validation.util";
import md5 from "md5";
import type React from "react";
import { useEffect, useState } from "react";

export type RegistrationUserCompanyAddress = {
  addressLine1: string;
  addressLine2?: string;
  officeParkName?: string;
  suburb?: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
  latitude: number;
  longitude: number;
};

export type RegistrationUserCompanyBranch = {
  id: string;
  name: string;
  address: RegistrationUserCompanyAddress;
};

export type RegistrationUserCompany = {
  id: string;
  name: string;
  registrationNo?: string;
  vatNo?: string;
  industryType: string;
  contactNumber: string;
  branches: RegistrationUserCompanyBranch[];
};

export type RegistrationUserDetails = {
  email: string;
  password: string;
  confirmPassword: string;
  fullName: string;
  phone: string;
  title: "Mr." | "Mrs." | "Ms." | "Dr." | "Prof.";
  designation?: string;
  preferredLanguage: string;
  preferredCommunicationMethods: Array<"email" | "sms">;
};

export type ValidationErrors = {
  email?: string;
  password?: string;
  confirmPassword?: string;
  fullName?: string;
  companies?: string;
  general?: string;
  phone?: string;
  preferredLanguage?: string;
  preferredCommunicationMethods?: string;
  consentTerms?: string;
  consentPrivacy?: string;
};

// Predefined options for select fields, required for validation and UI consistency
const TITLE_OPTIONS = ["Mr.", "Mrs.", "Ms.", "Dr.", "Prof."] as const;

type RegistrationFormProps = {};

const RegistrationForm: React.FC<RegistrationFormProps> = () => {
  const [userDetails, setUserDetails] = useState<RegistrationUserDetails>({
    email: "",
    password: "",
    confirmPassword: "",
    fullName: "",
    phone: "",
    title: "Mr.",
    designation: "",
    preferredLanguage: "en",
    preferredCommunicationMethods: ["email"],
  });

  const [companyType, setCompanyType] = useState<"private" | "business">(
    "business"
  );
  const [companies, setCompanies] = useState<RegistrationUserCompany[]>([]);
  const [activeCompanyId, setActiveCompanyId] = useState<string>("");
  const [activeBranchId, setActiveBranchId] = useState<string>("");
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [consent, setConsent] = useState({
    terms: false,
    privacy: false,
    marketing: false,
  });

  // Initialize with default "Private" company
  useEffect(() => {
    if (companyType === "private" && companies.length === 0) {
      const privateCompany: RegistrationUserCompany = {
        id: "private-1",
        name: "Private",
        registrationNo: undefined,
        vatNo: undefined,
        industryType: "Individual",
        contactNumber: "",
        branches: [
          {
            id: "branch-1",
            name: "Main Address",
            address: { ...EMPTYADDRESS },
          },
        ],
      };
      setCompanies([privateCompany]);
      setActiveCompanyId("private-1");
      setActiveBranchId("branch-1");
    } else if (companyType === "business" && companies.length === 0) {
      const businessCompany: RegistrationUserCompany = {
        id: "business-1",
        name: "",
        registrationNo: "",
        vatNo: "",
        industryType: "Miscellaneous / Other",
        contactNumber: "",
        branches: [
          {
            id: "branch-1",
            name: "Main Branch",
            address: { ...EMPTYADDRESS },
          },
        ],
      };
      setCompanies([businessCompany]);
      setActiveCompanyId("business-1");
      setActiveBranchId("branch-1");
    }
  }, [companyType, companies.length]);

  const validateEmail = (email: string): boolean => {
    // Use a stricter email regex for better validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 255;
  };

  const validatePhoneNumber = (phone: string): boolean => {
    const trimmedMobileNumber = phone.trim();
    // contains only digits or plus sign
    // 9-15 characters long
    const phoneRegex = /^\+?[0-9\s]{9,15}$/;
    return phoneRegex.test(phone) && trimmedMobileNumber.length <= 15;
  };

  const validateVAT = (vat?: string): boolean => {
    if (!vat) return true;
    return /^(\d{10}|[A-Za-z0-9-]{1,20})$/.test(vat) && vat.length <= 20;
  };

  const validatePostalCode = (postalCode: string): boolean => {
    // Accepts 4-10 alphanumeric, can be adjusted for country-specific rules
    return /^[A-Za-z0-9\- ]{1,10}$/.test(postalCode);
  };

  const validateAlpha = (value: string, max: number): boolean => {
    // Only alphabetic and spaces, up to max length
    return /^[A-Za-z\s]+$/.test(value) && value.length <= max;
  };

  const validateMaxLength = (value: string, max: number): boolean => {
    // Check if value is not empty and does not exceed max length
    return value.trim().length > 0 && value.length <= max;
  };

  const validateAlphaNum = (value: string, max: number): boolean => {
    // Alphanumeric and spaces, up to max length
    return /^[A-Za-z0-9\s]+$/.test(value) && value.length <= max;
  };

  const validateAddress = (
    address: RegistrationUserCompanyAddress
  ): boolean => {
    // Validate all address fields as per requirements
    return (
      !!address.addressLine1 &&
      address.addressLine1.length <= 255 &&
      !!address.suburb &&
      address.suburb.length <= 150 &&
      !!address.city &&
      /^[A-Za-z\s]+$/.test(address.city) &&
      address.city.length <= 100 &&
      !!address.province &&
      /^[A-Za-z\s]+$/.test(address.province) &&
      address.province.length <= 100 &&
      !!address.postalCode &&
      validatePostalCode(address.postalCode) &&
      address.postalCode.length <= 10 &&
      !!address.country &&
      COUNTRIES.some((c) => c.name === address.country)
    );
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // Title: not required, but must be from predefined options if present
    if (userDetails.title && !TITLE_OPTIONS.includes(userDetails.title)) {
      newErrors.fullName = "Invalid title selected";
    }

    // Full Name: required, max 100, only alphabetic and spaces
    if (!userDetails.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    } else if (!validateMaxLength(userDetails.fullName.trim(), 100)) {
      newErrors.fullName =
        "Full name must be alphabetic and up to 100 characters";
    }

    // Designation: optional, max 50
    if (userDetails.designation && userDetails.designation.length > 50) {
      newErrors.fullName = "Designation must be 50 characters or less";
    }

    // Email: required, valid, max 255
    if (!userDetails.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(userDetails.email)) {
      newErrors.email = "Please enter a valid email address (max 255 chars)";
    }
    // Note: uniqueness must be checked server-side

    // Phone: required, valid, max 15
    if (!userDetails.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!validatePhoneNumber(userDetails.phone)) {
      newErrors.phone = "Please enter a valid phone number (max 15 chars)";
    }

    // Username: optional, max 50, only if not using email as login (not implemented in UI)
    // if (userDetails.username && !validateUsername(userDetails.username)) {
    //   newErrors.general = "Username must be alphanumeric/underscore, max 50 chars";
    // }

    // Password: required, min 8, max 128, at least 1 uppercase, 1 special char
    if (!userDetails.password) {
      newErrors.password = "Password is required";
    } else if (!validatePassword(userDetails.password)) {
      newErrors.password =
        "Password must be 8-128 chars, include 1 uppercase and 1 special character";
    }

    // Confirm Password: required, max 128, must match
    if (!userDetails.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (userDetails.confirmPassword !== userDetails.password) {
      newErrors.confirmPassword = "Passwords do not match";
    } else if (userDetails.confirmPassword.length > 128) {
      newErrors.confirmPassword = "Confirm password too long";
    }

    // Validate preferred language
    if (
      !userDetails.preferredLanguage ||
      !LANGUAGES.some((lang) => lang.code === userDetails.preferredLanguage)
    ) {
      newErrors.preferredLanguage =
        "Preferred language is required and must be from the list";
    }

    // Validate preferred communication method
    if (
      userDetails.preferredCommunicationMethods.length === 0 ||
      !userDetails.preferredCommunicationMethods.every((method) =>
        ["email", "sms"].includes(method)
      )
    ) {
      newErrors.preferredCommunicationMethods =
        "At least one method is required";
    }

    // Consent: terms and privacy are required, marketing is optional
    if (!consent.terms) {
      newErrors.consentTerms = "You must agree to the Terms & Conditions";
    }
    if (!consent.privacy) {
      newErrors.consentPrivacy = "You must agree to the Privacy Policy";
    }

    // Company and branch validation
    if (companies.length === 0) {
      newErrors.companies = "At least one company is required";
    } else {
      for (const company of companies) {
        // Company Name: required if business, max 150
        if (
          companyType === "business" &&
          (!company.name.trim() || company.name.length > 150)
        ) {
          newErrors.companies = "Company name is required (max 150 chars)";
          break;
        }
        // Registration No: optional, max 50
        if (company.registrationNo && company.registrationNo.length > 50) {
          newErrors.companies =
            "Registration number must be 50 characters or less";
          break;
        }
        // VAT: optional, max 20, must match format
        if (
          company.vatNo &&
          (!validateVAT(company.vatNo) || company.vatNo.length > 20)
        ) {
          newErrors.companies = "Invalid VAT number format (max 20 chars)";
          break;
        }
        // Industry Type: required, must be from predefined
        if (
          companyType === "business" &&
          (!company.industryType ||
            !INDUSTRY_TYPES.includes(company.industryType as any))
        ) {
          newErrors.companies = "Industry type is required";
          break;
        }
        // Company Contact Number: required, valid, max 15
        if (
          companyType === "business" &&
          (!company.contactNumber ||
            !validatePhoneNumber(company.contactNumber))
        ) {
          newErrors.companies =
            "Company contact number required and valid (max 15 chars)";
          break;
        }
        // Branches: at least one, and all required address fields
        if (company.branches.length === 0) {
          newErrors.companies = "Each company must have at least one branch";
          break;
        }
        for (const branch of company.branches) {
          const addr = branch.address;
          // Street Address 1: required, max 255
          if (!addr.addressLine1 || addr.addressLine1.length > 255) {
            newErrors.companies = "Street Address 1 required (max 255 chars)";
            break;
          }
          // Street Address 2: optional, max 255
          if (addr.addressLine2 && addr.addressLine2.length > 255) {
            newErrors.companies = "Street Address 2 max 255 chars";
            break;
          }
          // Office Park Name: optional, max 150
          if (addr.officeParkName && addr.officeParkName.length > 150) {
            newErrors.companies = "Office Park Name max 150 chars";
            break;
          }
          // Suburb: required, max 150
          if (!addr.suburb || addr.suburb.length > 150) {
            newErrors.companies = "Suburb required (max 150 chars)";
            break;
          }
          // City: required, only alphabetic, max 100
          if (!addr.city || !validateMaxLength(addr.city, 100)) {
            newErrors.companies = "City required, (max 100 chars)";
            break;
          }
          // Province: required, only alphabetic, max 100
          if (!addr.province || !validateMaxLength(addr.province, 100)) {
            newErrors.companies = "Province required, (max 100 chars)";
            break;
          }
          // Postal Code: required, max 10, valid format
          if (
            !addr.postalCode ||
            !validatePostalCode(addr.postalCode) ||
            addr.postalCode.length > 10
          ) {
            newErrors.companies = "Postal code required, valid (max 10 chars)";
            break;
          }
          // Country: required, from predefined list
          if (
            !addr.country ||
            !COUNTRIES.some((c) => c.name === addr.country)
          ) {
            newErrors.companies = "Country required, select from list";
            break;
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // province and country are required for SendRegisterUserAddress to ensure backend receives complete address info

      const hashPassword = md5(userDetails.password);

      if (!hashPassword) {
        setErrors({ general: "Password hashing failed." });
        return;
      }

      const newSendCompanies: SendRegisterUserCompany[] = companies.map(
        (company) => ({
          name: company.name,
          type: companyType,
          industryType: company.industryType,
          phoneNumber: company.contactNumber || userDetails.phone,
          registrationNumber: company.registrationNo || undefined,
          taxNumber: company.vatNo || undefined,
          branches: company.branches.map((branch) => ({
            name: branch.name,
            city: branch.address.city,
            latitude: branch.address.latitude.toString(),
            longitude: branch.address.longitude.toString(),
            address1: branch.address.addressLine1,
            pinCode: branch.address.postalCode,
            suburb: branch.address.suburb || undefined,
            address2: branch.address.addressLine2 || undefined,
            officeParkName: branch.address.officeParkName || undefined,
            province: branch.address.province,
            country: branch.address.country,
          })),
        })
      );

      const registrationData: SendRegisterUser = {
        email: userDetails.email,
        password: hashPassword,
        fullName: userDetails.fullName,
        phoneNumber: userDetails.phone,
        companies: newSendCompanies,
        roleID: undefined, // Assuming roleID is optional
        title: userDetails.title,
        designation: userDetails.designation || "",
        preferredLanguage: userDetails.preferredLanguage,
        preferredCommunicationMethods:
          userDetails.preferredCommunicationMethods,
        marketingOptIn: consent.marketing,
      };

      const response = await sendRegisterUser(registrationData);

      if (response.rStatus === "error") {
        setErrors({ general: response.rData });
        return;
      }
      setRegistrationSuccess(true);
    } catch (error) {
      console.error("Registration error:", error);
      setErrors({ general: "Registration failed. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return registrationSuccess ? (
    <div className="mx-auto max-w-md">
      <Card>
        <CardContent>
          <h2 className="text-lg font-semibold text-green-600">
            Registration Successful!
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Thank you for registering. An admin will review your details and
            verify your account shortly. You will receive an email notification
            once your account is approved.
          </p>
        </CardContent>
      </Card>
    </div>
  ) : (
    <div className="mx-auto">
      <Card className="border-none">
        {/* <CardHeader>
          <CardTitle>Create Account</CardTitle>
          <CardDescription>
            Please fill in your details to create a new account
          </CardDescription>
        </CardHeader> */}
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* User Details Section */}
            <UserDetails
              userDetails={userDetails}
              setUserDetails={setUserDetails}
              errors={errors}
            />
            <UserCompanies
              companies={companies}
              setCompanies={setCompanies}
              activeCompanyId={activeCompanyId}
              setActiveCompanyId={setActiveCompanyId}
              activeBranchId={activeBranchId}
              setActiveBranchId={setActiveBranchId}
              companyType={companyType}
              setCompanyType={setCompanyType}
              errors={errors}
            />
            <UserConsent
              consent={consent}
              setConsent={setConsent}
              errors={errors}
            />
            {(errors.companies ||
              errors.confirmPassword ||
              errors.password ||
              errors.email ||
              errors.fullName ||
              errors.phone) && (
              <Alert variant="destructive">
                <AlertDescription className="!text-red-500 opacity-95">
                  Please review errors/requirements in the form
                </AlertDescription>
              </Alert>
            )}

            {errors.general && (
              <Alert variant="destructive">
                <AlertDescription className="!text-red-500">
                  {errors.general}
                </AlertDescription>
              </Alert>
            )}

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Creating Account..." : "Create Account"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegistrationForm;
